import { isNativeApp, VIDEO_EMBED_ORIGIN } from './nativePlatform';

export type CitationKind = 'video' | 'audio' | 'recipe' | 'article' | 'meeting';

export const detectKindFromUrl = (url: string): CitationKind => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();
    const hostname = urlObj.hostname.toLowerCase();

    // Check path-based indicators first
    if (pathname.includes('/video/') || pathname.includes('/videos/')) return 'video';
    if (pathname.includes('/audio/') || pathname.includes('/podcast/') || pathname.includes('/listen/')) return 'audio';
    if (pathname.includes('/recipe/') || pathname.includes('/recipes/')) return 'recipe';
    if (pathname.includes('/meeting/') || pathname.includes('/meetings/') || pathname.includes('/call/')) return 'meeting';

    // Check domain-based indicators
    if (hostname.includes('youtube') || hostname.includes('vimeo') || hostname.includes('video')) return 'video';
    if (hostname.includes('spotify') || hostname.includes('soundcloud') || hostname.includes('podcast')) return 'audio';
    if (hostname.includes('recipe') || hostname.includes('cooking') || hostname.includes('food')) return 'recipe';
    if (hostname.includes('zoom') || hostname.includes('teams') || hostname.includes('meet')) return 'meeting';

    // Default to article for other content
    return 'article';
  } catch {
    return 'article';
  }
};

export const getKindDisplayName = (kind: CitationKind): string => {
  const names: Record<CitationKind, string> = {
    video: 'Video',
    audio: 'Audio',
    recipe: 'Recipe',
    article: 'Article',
    meeting: 'Meeting'
  };
  return names[kind];
};

export const buildVimeoEmbedUrl = (vimeoId: string): string => {
  // Native WebView origin (capacitor://localhost) is not on Vimeo's embed allowlist, which
  // triggers "because of privacy settings this video cannot be played here". In the native
  // shell, route through the HTTPS wrapper page hosted on an allowlisted web origin so Vimeo
  // sees an allowed referrer. Web keeps embedding the player directly (origin already allowed).
  if (isNativeApp()) {
    return buildVideoEmbedWrapperUrl(vimeoId);
  }
  return `https://player.vimeo.com/video/${encodeURIComponent(vimeoId)}`;
};

// Builds the HTTPS wrapper URL (video-embed.html) from a Vimeo id, which may optionally be
// supplied as "id/hash" for unlisted videos. Inputs are sanitized to digits / alphanumerics.
export const buildVideoEmbedWrapperUrl = (vimeoId: string): string => {
  const [rawId, rawHash] = String(vimeoId).split('/');
  const id = (rawId || '').replace(/[^0-9]/g, '');
  const hash = (rawHash || '').replace(/[^a-zA-Z0-9]/g, '');
  const params = new URLSearchParams({ id });
  if (hash) {
    params.set('h', hash);
  }
  return `${VIDEO_EMBED_ORIGIN}/video-embed.html?${params.toString()}`;
};

// Parses a Vimeo watch/player URL into its numeric id and optional privacy hash.
const parseVimeoUrl = (url: string): { id: string; hash?: string } | null => {
  try {
    const u = new URL(url);
    if (!u.hostname.toLowerCase().includes('vimeo.com')) return null;

    // player.vimeo.com/video/{id}?h={hash}
    const playerMatch = u.pathname.match(/\/video\/(\d+)/);
    if (playerMatch) {
      return { id: playerMatch[1], hash: u.searchParams.get('h') || undefined };
    }

    // vimeo.com/{id}[/{hash}]
    const segments = u.pathname.split('/').filter(Boolean);
    if (segments.length > 0 && /^\d+$/.test(segments[0])) {
      return { id: segments[0], hash: segments[1] };
    }
    return null;
  } catch {
    return null;
  }
};

// For arbitrary external URLs (e.g. content/link modals): on native, rewrite Vimeo links to the
// HTTPS wrapper so playback isn't blocked by the WebView origin. Non-Vimeo URLs are unchanged.
export const wrapVimeoUrlForNative = (url: string): string => {
  if (!isNativeApp()) return url;
  const parsed = parseVimeoUrl(url);
  if (!parsed) return url;
  return buildVideoEmbedWrapperUrl(parsed.hash ? `${parsed.id}/${parsed.hash}` : parsed.id);
};

export const buildSimplecastEmbedUrl = (podcastProviderId: string): string =>
  `https://player.simplecast.com/${encodeURIComponent(podcastProviderId)}?dark=true&hide_share=true&color=08364C`;
